/* eslint react/prop-types: 0 */
import React, { useEffect } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import clsx from 'clsx';

import { ResizeObserver } from 'resize-observer';

import colors from '../../../theme/colors';

const STEP_SEPARATION = 22;

const useStyles = makeStyles({
  stepperContainer: {
    position: 'relative',
  },
  stepDot: {
    width: 18,
    height: 18,
    borderRadius: 9,
    marginRight: 24,
    position: 'relative',
    zIndex: 3,
  },
  completedStepDot: {
    backgroundColor: colors.madison,
  },
  activeStepDot: {
    backgroundColor: colors.lightning_yellow,
  },
  pendingStepDot: {
    backgroundColor: colors.silver,
  },

  stepElementWrapper: {
    position: 'relative',
  },
  stepConnector: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 8,
    width: 0,
    zIndex: 2,
    borderLeft: `2px solid ${colors.madison}`,
  },
  transitionActiveStepConnector: {
    bottom: '50%',
  },
  transitionInnactiveStepConnector: {
    top: '50%',
    borderColor: colors.silver,
  },
  innactiveStepConnector: {
    borderColor: colors.silver,
  },
  disabledStepConnector: {
    borderLeft: 'none',
  },
  auxBackgroundDisabledConnector: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 8,
    width: 0,
    borderLeft: `2px dashed ${colors.silver}`,
  },

  firstStepConnector: {
    top: '50%',
  },
  lastStepConnector: {
    bottom: '50%',
  },
  stepSeparation: {
    height: STEP_SEPARATION,
    position: 'relative',
  },
});

export type VerticalStepperProps = {
  steps: React.ReactNode[];
  activeStep: number;
  disabled?: boolean;
};

/**
 * This method forces the provided dottedLineDiv to have a maximum size where it is contained inside
 * the same bounds as the topDiv and bottomDiv, without being on the same branch of the dom tree.
 *
 * @param topDiv - The div representing the first connector.
 * @param botDiv - The div representing the last connector.
 * @param dottedLineDiv - The div representing the special line used to render disabled.
 */
export function adjustDottedLine(
  topDiv: HTMLDivElement,
  botDiv: HTMLDivElement,
  dottedLineDiv: HTMLDivElement,
): void {
  const topOffset = topDiv.offsetTop;
  const botOffset = botDiv.offsetHeight;
  dottedLineDiv.setAttribute(
    'style',
    `
      top: ${topOffset}px;
      bottom: ${botOffset}px;
    `,
  );
}

/**
 * Applies the dotted line adjustments ONLY if the provided elements are not null, and also creates an instance
 * of a ResizeObserver which will auto adjust the size every time the bottom box changes dimensions.
 *
 * @param topDiv - The div representing the first connector (or null on the first render)
 * @param botDiv - The div representing the last connector (or null on the first render)
 * @param dottedLineDiv - The div representing the special line used to render disabled (or null on the first render)
 */
export function modifyAndListenForDottedLine(
  topDiv: HTMLDivElement | null,
  botDiv: HTMLDivElement | null,
  dottedLineDiv: HTMLDivElement | null,
): void {
  if (topDiv && botDiv && dottedLineDiv) {
    adjustDottedLine(topDiv, botDiv, dottedLineDiv);
    new ResizeObserver((): void =>
      adjustDottedLine(topDiv, botDiv, dottedLineDiv),
    ).observe(botDiv);
  }
}

const VerticalStepper = ({
  steps,
  activeStep,
  disabled = false,
}: VerticalStepperProps): JSX.Element => {
  const classes = useStyles();

  /** useRef via React to that we can jest.spy mock them */
  const topConnectorRef = React.useRef<HTMLDivElement>(null);
  const botConnectorRef = React.useRef<HTMLDivElement>(null);
  const dottedConnectorRef = React.useRef<HTMLDivElement>(null);

  /**
   * Returns a ReactNode that represents the steps 'dot' decoration, depending on
   * whether the step is active, pending or completed
   *
   * @param stepOffset
   */
  function makeDotForStep(stepOffset: number): React.ReactNode {
    let additionalDotClass = classes.completedStepDot;
    if (stepOffset === activeStep && !disabled) {
      additionalDotClass = classes.activeStepDot;
    } else if (stepOffset > activeStep) {
      additionalDotClass = classes.pendingStepDot;
    }
    return (
      <div
        data-testid="dot"
        className={clsx(classes.stepDot, additionalDotClass)}
      />
    );
  }

  /**
   * The "dashed" line used to mark the stepper as "innactive" is a separate div from the ones we use to
   * draw the regular line. This is because the line needs to use a dash display type, and since the regular line is
   * actually made of several small lines, we need to render a single entity rather than one line for each
   * breakpoint (else the points where the different lines "join" is clearly visible). Since the top and botton
   * space can change based on the first and last element's height, we use this hook to both set the initial
   * value and listen for any resize events in case the cards change.
   */
  useEffect(() => {
    modifyAndListenForDottedLine(
      topConnectorRef.current,
      botConnectorRef.current,
      dottedConnectorRef.current,
    );
  }, [topConnectorRef, botConnectorRef, dottedConnectorRef]);

  /**
   * Returns a react node that represents a connector for a single step. This connector
   * note is a line that connects this offset's dot with both the previous and the next
   * dots (if applicable).
   *
   * @param stepOffset
   */
  function makeConnectorForStep(stepOffset: number): React.ReactNode {
    let ref: undefined | React.MutableRefObject<HTMLDivElement>;
    if (stepOffset === 0) {
      ref = topConnectorRef;
    } else if (stepOffset >= steps.length - 1) {
      ref = botConnectorRef;
    }

    let connector = (
      <div
        ref={ref}
        data-testid="defaultStepConnector"
        className={clsx(
          classes.stepConnector,
          stepOffset === 0 ? classes.firstStepConnector : '',
        )}
      />
    );
    if (stepOffset === activeStep) {
      connector = (
        <>
          {stepOffset > 0 && (
            <div
              ref={ref}
              data-testid="activeStepConnectorTop"
              className={clsx(
                classes.stepConnector,
                classes.transitionActiveStepConnector,
              )}
            />
          )}
          {stepOffset < steps.length - 1 && (
            <div
              ref={ref}
              data-testid="activeStepConnectorBottom"
              className={clsx(
                classes.stepConnector,
                classes.transitionInnactiveStepConnector,
                disabled ? classes.disabledStepConnector : '',
              )}
            />
          )}
        </>
      );
    } else if (stepOffset > activeStep) {
      connector = (
        <div
          ref={ref}
          data-testid="innactiveStepConnector"
          className={clsx(
            classes.stepConnector,
            classes.innactiveStepConnector,
            stepOffset >= steps.length - 1 ? classes.lastStepConnector : '',
            disabled ? classes.disabledStepConnector : '',
          )}
        />
      );
    }
    return connector;
  }

  /**
   * Steps are separated by a margin, and this "accessory connector" prevents this margin
   * from breaking the consistency between the connecting lines.
   *
   * @param stepOffset
   */
  function makeAccessoryConnectorForStep(stepOffset: number): React.ReactNode {
    const accessoryConnectorTestId = 'accessoryConnector';
    if (stepOffset >= steps.length - 1) {
      return <div data-testid={accessoryConnectorTestId} />;
    }
    let accessoryConnector = (
      <div
        data-testid={accessoryConnectorTestId}
        className={classes.stepConnector}
      />
    );
    if (stepOffset >= activeStep) {
      accessoryConnector = (
        <div
          data-testid={accessoryConnectorTestId}
          className={clsx(
            classes.stepConnector,
            classes.innactiveStepConnector,
            disabled ? classes.disabledStepConnector : '',
          )}
        />
      );
    }
    return (
      <Grid item xs={12}>
        <div className={classes.stepSeparation}>{accessoryConnector}</div>
      </Grid>
    );
  }

  return (
    <Grid container style={{ position: 'relative' }}>
      {disabled && (
        <div
          data-testid="dotted-connector"
          ref={dottedConnectorRef}
          className={classes.auxBackgroundDisabledConnector}
        />
      )}
      {steps.map(
        (step, index): React.ReactNode => {
          const dot = makeDotForStep(index);
          const connector = makeConnectorForStep(index);
          const accessoryConnector = makeAccessoryConnectorForStep(index);

          return (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              <Grid
                className={classes.stepElementWrapper}
                item
                xs={12}
                container
                alignItems="center"
                justifyContent="flex-start"
              >
                {connector}
                {dot}
                <Grid item xs>
                  {step}
                </Grid>
              </Grid>
              {accessoryConnector}
            </React.Fragment>
          );
        },
      )}
    </Grid>
  );
};

export default VerticalStepper;
